import { useEffect } from 'react';

import { useAnnotationManager } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';

export function usePreemptiveGate({ onConfirm }: { onConfirm: () => void }): void {
	const annotationManager = useAnnotationManager();
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const { showModal } = useDialogs();

	// This hook enables the new preemptive gate for the editor comment API manager to catch the situation
	// where the user tries to start a draft while there is unsaved content in the editor.
	useEffect(() => {
		if (annotationManager) {
			if (hasContentChanged) {
				annotationManager.setPreemptiveGate(() => {
					return new Promise((resolve) => {
						showModal(CommentWarningDialog, {
							onConfirm: () => {
								resetContentChanged();

								onConfirm();

								// Allow the draft to be started or the comment viewed.
								resolve(true);
							},
							onCancel: () => {
								// Cancel the start draft or view attempt.
								resolve(false);
							},
							onClose: () => {
								// Just in case the user closes the modal by some other means we need to ensure the
								// promise is resolved. We do not want to leave the preemptive gate open.
								resolve(false);
							},
						});
					});
				});
			} else {
				annotationManager?.setPreemptiveGate(() => Promise.resolve(true));
			}
			return () => {
				annotationManager?.setPreemptiveGate(() => Promise.resolve(true));
			};
		}
	}, [hasContentChanged, annotationManager, showModal, resetContentChanged, onConfirm]);
}
